import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { getActivityColor, formatTimeAgo } from '../../utils/dashboardHelpers'


const RecentActivity = ({ activities, isLoading, isError }) => {
  const navigate = useNavigate()

  const handleActivityClick = (activity) => {
    if (activity.link) {
      navigate(activity.link)
    } else if (activity.metadata?.scanId) {
      navigate('/integrations')
    } else if (activity.metadata?.connectionId) {
      navigate('/integrations')
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:history" className="text-gray-600 text-xl" />
          <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
        </div>
      </div>

      {/* Content */}
      <div className="divide-y divide-gray-100">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg animate-pulse">
                <div className="w-9 h-9 rounded-lg bg-gray-200 flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="flex gap-2">
                    <div className="h-5 bg-gray-200 rounded w-20"></div>
                    <div className="h-5 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
              <Icon icon="mdi:alert-circle" className="text-2xl text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">Unable to load recent activity</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
              <Icon icon="mdi:inbox-outline" className="text-2xl text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-800 mb-1">No recent activity</p>
            <p className="text-xs text-gray-500">Your activities will appear here</p>
          </div>
        ) : (
          activities.map((activity) => {
            const url = activity.metadata?.url
            const websiteName = activity.metadata?.websiteName
            const totalIssues = activity.metadata?.totalIssues ?? null
            const totalViolations = activity.metadata?.violations ?? null

            return (
              <div
                key={activity.id}
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleActivityClick(activity)}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-lg ${getActivityColor(activity.type)} flex items-center justify-center text-white flex-shrink-0 mt-0.5`}>
                    <Icon icon={activity.icon} className="text-lg" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Title + score row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-sm text-gray-800 truncate">
                          {activity.action}
                        </h3>
                        {(url || websiteName) && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            {websiteName && <span className="font-medium text-gray-500">{websiteName} &bull; </span>}
                            {url}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Stats badges */}
                    {(totalIssues !== null || totalViolations !== null) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {totalIssues !== null && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-50 rounded text-orange-600 font-medium">
                            <Icon icon="mdi:alert-circle-outline" className="text-sm" />
                            {totalIssues} issues
                          </span>
                        )}
                        {totalViolations !== null && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-red-50 rounded text-red-600">
                            <Icon icon="mdi:alert" className="text-sm" />
                            {totalViolations} violations
                          </span>
                        )}
                      </div>
                    )}

                    {/* Timestamp + button row */}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-400">
                        {activity.description && (
                          <span className="text-gray-500 mr-1">{activity.description} &bull;</span>
                        )}
                        {formatTimeAgo(activity.timestamp)}
                      </p>
                      {/* {activity.button && (
                        <button
                          className="flex-shrink-0 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleActivityClick(activity)
                          }}
                        >
                          {activity.button}
                        </button>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default RecentActivity
